import { type ApiError, HttpApiService } from "@/shared/services/httpApi.service";

/** Saves a blob the browser already holds — a file the API answered with `responseType: "blob"`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * The file name a `Content-Disposition: attachment` header carries, in either of its two spellings:
 * `filename="lote.xlsx"` or the RFC 5987 `filename*=UTF-8''lote%20q3.xlsx`. Null when the header is missing
 * or names nothing, so the caller falls back to a name of its own.
 */
export function parseContentDispositionFilename(header: string | null | undefined): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*(?:[\w-]+'[\w-]*')?([^;]+)/i.exec(header);
  if (extended) {
    const raw = extended[1].trim().replace(/^"(.*)"$/, "$1");
    try {
      const decoded = decodeURIComponent(raw).trim();
      if (decoded) return decoded;
    } catch {
      if (raw) return raw;
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|([^;]+))/i.exec(header);
  if (!plain) return null;
  const name = (plain[2] ?? plain[3] ?? "").trim();
  return name || null;
}

/**
 * A request made with `responseType: "blob"` receives its ERROR body as a blob too, so `readApiCode` and
 * `readApiMessages` see nothing until the blob is read back into JSON. Awaiting this once in the catch block
 * restores the usual `{ message, code }` shape in place and hands the same error back.
 */
export async function normalizeBlobError(error: unknown): Promise<unknown> {
  if (!HttpApiService.isAxiosError(error) || !error.response) return error;
  const body: unknown = error.response.data;
  if (!(body instanceof Blob)) return error;
  try {
    const text = await body.text();
    error.response.data = JSON.parse(text) as ApiError;
  } catch {
    error.response.data = { message: "" };
  }
  return error;
}
